import React, {useEffect, useState} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {Button, DatePicker, Empty, message, Modal, Spin, Steps, Tabs, Tag, Tooltip} from 'antd';
import {
    DeleteOutlined,
    LineChartOutlined,
    PauseOutlined,
    PlayCircleOutlined,
    PlusOutlined,
    ReloadOutlined
} from '@ant-design/icons';
import {backtestService, BacktestTaskResponse} from '../../services/backtestService';
import {MyStrategyItem, myStrategyService} from '../../services/myStrategyService';
import dayjs from 'dayjs';
import './BacktestPage.css';

const {RangePicker} = DatePicker;

const BacktestPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState<string>(() => searchParams.get('tab') || 'running');
    const [starting, setStarting] = useState(false);
    const [runningTasks, setRunningTasks] = useState<BacktestTaskResponse[]>([]);
    const [historyTasks, setHistoryTasks] = useState<BacktestTaskResponse[]>([]);
    const [totalRunning, setTotalRunning] = useState(0);
    const [totalHistory, setTotalHistory] = useState(0);
    const [pageRunning, setPageRunning] = useState(1);
    const [pageHistory, setPageHistory] = useState(1);

    const [strategyConfigs, setStrategyConfigs] = useState<any[]>([]);
    const [defaultConfigId, setDefaultConfigId] = useState<number | undefined>();
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [createStep, setCreateStep] = useState(0);
    const [taskNameError, setTaskNameError] = useState<string | null>(null);
    const [createForm, setCreateForm] = useState({
        taskName: '',
        strategyConfigId: undefined as number | undefined,
        timeRange: [] as dayjs.Dayjs[] | [],
        timeframe: '5m'
    });

    useEffect(() => {
        loadConfigs();
        loadRunningTasks();
    }, []);

    // 轮询运行中的任务
    useEffect(() => {
        const hasActiveTasks = runningTasks.some(t => t.status === 'RUNNING' || t.status === 'PENDING');
        if (hasActiveTasks) {
            const timer = setInterval(() => {
                loadRunningTasks(false);
            }, 10000);
            return () => clearInterval(timer);
        }
    }, [runningTasks]);

    const loadConfigs = async () => {
        try {
            // 从我的策略列表获取数据
            const res = await myStrategyService.getMyStrategies(0, 100);
            const strategies = res?.list || [];
            setStrategyConfigs(strategies);
            // 获取默认配置（第一个策略）
            if (strategies.length > 0) {
                setDefaultConfigId(strategies[0].id);
            }
        } catch (e) {
            console.error('Failed to load strategies', e);
        }
    };

    const loadRunningTasks = async (showLoading = true) => {
        if (showLoading) setLoading(true);
        try {
            const res = await backtestService.getBacktests(undefined, 0, 100);
            const allTasks = res?.content || [];
            const running = allTasks.filter(t =>
                t.status === 'RUNNING' || t.status === 'PENDING' || t.status === 'PAUSED'
            );
            const history = allTasks.filter(t =>
                t.status === 'SUCCESS' || t.status === 'FAILED'
            );
            setRunningTasks(running);
            setHistoryTasks(history);
            setTotalRunning(running.length);
            setTotalHistory(history.length);
        } catch (e) {
            console.error('Failed to load tasks', e);
        } finally {
            if (showLoading) setLoading(false);
        }
    };

    const validateTaskName = async (name: string): Promise<boolean> => {
        if (!name || name.trim() === '') {
            setTaskNameError(null);
            return true;
        }
        try {
            const isValid = await backtestService.validateTaskName(name.trim());
            if (!isValid) {
                setTaskNameError('该任务名称已被使用');
            } else {
                setTaskNameError(null);
            }
            return isValid;
        } catch (e) {
            setTaskNameError(null);
            return true;
        }
    };

    const handleTaskNameChange = (name: string) => {
        setCreateForm({...createForm, taskName: name});
        if (taskNameError) {
            setTaskNameError(null);
        }
    };

    const handleTaskNameBlur = () => {
        if (createForm.taskName.trim()) {
            validateTaskName(createForm.taskName);
        }
    };

    const openCreateModal = () => {
        setCreateStep(0);
        setCreateForm({
            taskName: '',
            strategyConfigId: undefined,
            timeRange: [],
            timeframe: '5m'
        });
        setTaskNameError(null);
        setShowCreateModal(true);
    };

    const handleNextStep = () => {
        if (createStep === 0) {
            // 验证第一步
            if (!createForm.strategyConfigId) {
                message.error('请选择策略');
                return;
            }
            if (createForm.taskName.trim()) {
                validateTaskName(createForm.taskName).then(isValid => {
                    if (isValid) {
                        setCreateStep(1);
                    }
                });
            } else {
                setCreateStep(1);
            }
        }
    };

    const handlePrevStep = () => {
        setCreateStep(0);
    };

    const handleCreateBacktest = async () => {
        if (!createForm.strategyConfigId || createForm.timeRange.length !== 2) {
            message.error('请填写完整信息');
            return;
        }

        const [startDate, endDate] = createForm.timeRange;
        const timeRange = `${startDate.format('YYYYMMDD')}-${endDate.format('YYYYMMDD')}`;

        setStarting(true);
        try {
            await backtestService.createBacktest({
                taskName: createForm.taskName.trim() || `回测任务_${Date.now()}`,
                strategyConfigId: createForm.strategyConfigId,
                timeRange,
                timeframe: createForm.timeframe
            });
            message.success('回测任务已启动');
            setShowCreateModal(false);
            loadRunningTasks();
        } catch (e: any) {
            message.error(e.message || '启动失败');
        } finally {
            setStarting(false);
        }
    };

    const handlePause = async (id: number) => {
        try {
            await backtestService.pauseBacktest(id);
            message.success('已暂停');
            loadRunningTasks();
        } catch (e) {
            message.error('暂停失败');
        }
    };

    const handleResume = async (id: number) => {
        try {
            await backtestService.resumeBacktest(id);
            message.success('已恢复');
            loadRunningTasks();
        } catch (e) {
            message.error('恢复失败');
        }
    };

    const handleDelete = async (id: number) => {
        try {
            await backtestService.deleteBacktest(id);
            message.success('删除成功');
            loadRunningTasks();
        } catch (e: any) {
            message.error(e.message || '删除失败');
        }
    };

    const getStatusTag = (status: string) => {
        switch (status) {
            case 'RUNNING':
                return <Tag color="blue">运行中</Tag>;
            case 'PENDING':
                return <Tag color="default">等待中</Tag>;
            case 'PAUSED':
                return <Tag color="orange">已暂停</Tag>;
            case 'SUCCESS':
                return <Tag color="green">成功</Tag>;
            case 'FAILED':
                return <Tag color="red">失败</Tag>;
            default:
                return <Tag>{status}</Tag>;
        }
    };

    const formatDate = (dateStr: string | undefined) => {
        if (!dateStr) return '-';
        const date = new Date(dateStr);
        return date.toLocaleDateString('zh-CN', {month: '2-digit', day: '2-digit'});
    };

    const renderTaskCard = (task: BacktestTaskResponse) => {
        const isRunning = task.status === 'RUNNING' || task.status === 'PENDING' || task.status === 'PAUSED';

        return (
            <div
                key={task.id}
                className="backtest-card"
                onClick={() => navigate(`/strategy/backtest/${task.id}`)}
            >
                <div className="backtest-card-header">
                    <div className="backtest-card-title">
                        <span className="task-name">{task.taskName || `回测任务 #${task.id}`}</span>
                        {getStatusTag(task.status)}
                    </div>
                    {isRunning && (
                        <div className="backtest-card-actions">
                            {task.status === 'PAUSED' ? (
                                <PlayCircleOutlined
                                    className="action-icon"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleResume(task.id);
                                    }}
                                />
                            ) : (
                                <PauseOutlined
                                    className="action-icon"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handlePause(task.id);
                                    }}
                                />
                            )}
                        </div>
                    )}
                    {!isRunning && (
                        <div className="backtest-card-actions">
                            <DeleteOutlined
                                className="action-icon danger"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleDelete(task.id);
                                }}
                            />
                        </div>
                    )}
                </div>

                <div className="backtest-card-info">
                    <div className="info-row">
                        <span className="info-label">策略</span>
                        <span className="info-value">{task.strategyName || '-'}</span>
                    </div>
                    <div className="info-row">
                        <span className="info-label">时间范围</span>
                        <span className="info-value mono">{task.timeRange}</span>
                    </div>
                    <div className="info-row">
                        <span className="info-label">周期</span>
                        <span className="info-value">{task.timeframe}</span>
                    </div>
                </div>

                {task.status === 'SUCCESS' && (
                    <div className="backtest-card-metrics">
                        <div className="metric">
                            <span className="metric-label">收益率</span>
                            <span className={`metric-value ${(task.totalProfitPct || 0) >= 0 ? 'pos' : 'neg'}`}>
                                {(task.totalProfitPct || 0) >= 0 ? '+' : ''}{task.totalProfitPct?.toFixed(2) || '0.00'}%
                            </span>
                        </div>
                        <div className="metric">
                            <span className="metric-label">胜率</span>
                            <span className="metric-value">{task.winRate?.toFixed(1) || '0.0'}%</span>
                        </div>
                        <div className="metric">
                            <span className="metric-label">交易数</span>
                            <span className="metric-value">{task.totalTrades || 0}</span>
                        </div>
                    </div>
                )}

                <div className="backtest-card-footer">
                    <span className="create-time">{formatDate(task.createdAt)}</span>
                    <LineChartOutlined className="detail-icon"/>
                </div>
            </div>
        );
    };

    const renderRunningTab = () => (
        <div className="tab-content">
            {loading ? (
                <div className="loading-container"><Spin/></div>
            ) : runningTasks.length === 0 ? (
                <Empty description="暂无正在运行的回测任务" image={Empty.PRESENTED_IMAGE_SIMPLE}/>
            ) : (
                <div className="backtest-grid">
                    {runningTasks.map(renderTaskCard)}
                </div>
            )}
        </div>
    );

    const renderHistoryTab = () => (
        <div className="tab-content">
            {loading ? (
                <div className="loading-container"><Spin/></div>
            ) : historyTasks.length === 0 ? (
                <Empty description="暂无历史回测记录" image={Empty.PRESENTED_IMAGE_SIMPLE}/>
            ) : (
                <div className="backtest-grid">
                    {historyTasks.map(renderTaskCard)}
                </div>
            )}
        </div>
    );

    const renderStepContent = () => {
        if (createStep === 0) {
            return (
                <div className="step-content">
                    <div className="form-item">
                        <label>任务名称</label>
                        <input
                            type="text"
                            placeholder="留空则自动生成"
                            value={createForm.taskName}
                            onChange={(e) => handleTaskNameChange(e.target.value)}
                            onBlur={handleTaskNameBlur}
                            className={taskNameError ? 'error' : ''}
                        />
                        {taskNameError && <span className="error-tip">{taskNameError}</span>}
                    </div>
                    <div className="form-item">
                        <label>选择策略 *</label>
                        <select
                            value={createForm.strategyConfigId || ''}
                            onChange={(e) => setCreateForm({...createForm, strategyConfigId: Number(e.target.value)})}
                        >
                            <option value="">请选择策略</option>
                            {strategyConfigs.map((s: MyStrategyItem) => (
                                <option key={s.id} value={s.id}>{s.title}</option>
                            ))}
                        </select>
                    </div>
                </div>
            );
        } else {
            return (
                <div className="step-content">
                    <div className="form-item">
                        <label>K线周期 *</label>
                        <select
                            value={createForm.timeframe}
                            onChange={(e) => setCreateForm({...createForm, timeframe: e.target.value})}
                        >
                            <option value="1m">1m</option>
                            <option value="5m">5m</option>
                            <option value="15m">15m</option>
                            <option value="1h">1h</option>
                            <option value="4h">4h</option>
                            <option value="1d">1d</option>
                        </select>
                    </div>
                    <div className="form-item">
                        <label>时间范围 *</label>
                        <RangePicker
                            value={createForm.timeRange}
                            onChange={(dates) => setCreateForm({...createForm, timeRange: dates || []})}
                            style={{width: '100%'}}
                        />
                    </div>
                </div>
            );
        }
    };

    return (
        <div className="backtest-page">
            <div className="page-header">
                <div className="header-left">
                    <h2>回测任务</h2>
                    {/*<p>运行回测，验证策略有效性</p>*/}
                </div>
                <div className="header-actions">
                    <Tooltip title="刷新" overlayStyle={{zIndex: 1300}}>
                        <button className="btnIcon" onClick={() => loadRunningTasks()}>
                            <ReloadOutlined/>
                        </button>
                    </Tooltip>
                    <Tooltip title="新建回测" overlayStyle={{zIndex: 1300}}>
                        <button className="btnGold" onClick={openCreateModal}>
                            <PlusOutlined/>
                        </button>
                    </Tooltip>
                </div>
            </div>

            <div className="tabs-container">
                <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    items={[
                        {key: 'running', label: `正在运行 (${totalRunning})`, children: renderRunningTab()},
                        {key: 'history', label: `历史回测 (${totalHistory})`, children: renderHistoryTab()}
                    ]}
                />
            </div>

            <Modal
                title={
                    <div className="modal-title">
                        <span>新建回测任务</span>
                        <Steps
                            current={createStep}
                            size="small"
                            items={[
                                {title: '基础配置'},
                                {title: '回测参数'}
                            ]}
                        />
                    </div>
                }
                open={showCreateModal}
                onCancel={() => setShowCreateModal(false)}
                footer={
                    <div className="modal-footer">
                        {createStep === 0 ? (
                            <>
                                <Button onClick={() => setShowCreateModal(false)}>取消</Button>
                                <Button type="primary" onClick={handleNextStep}>下一步</Button>
                            </>
                        ) : (
                            <>
                                <Button onClick={handlePrevStep}>上一步</Button>
                                <Button type="primary" onClick={handleCreateBacktest} loading={starting}>
                                    启动回测
                                </Button>
                            </>
                        )}
                    </div>
                }
                width={520}
            >
                {renderStepContent()}
            </Modal>
        </div>
    );
};

export default BacktestPage;
